// ATE-CR-159 — Admin page at /admin/notifications/verifications.
//
// Lets a SUPER_ADMIN pick a recipient from the helm-managed allowlist,
// publish a synthetic verification message, then watch the 5-stage
// pipeline progress in a vertical stepper. The status endpoint may
// return Retry-After: 5 — when present, schedule the next auto-refresh
// that many seconds out (no recursion / no react-query; plain
// useEffect + setTimeout, mirroring useJobPolling.ts).
//
// SUPER_ADMIN gating mirrors EscalationsListPage: page-level guard
// with a 403-style message; if the user role isn't SUPER_ADMIN, the
// page never calls the API. Backend also gates (require_super_admin)
// — defence in depth.
import { useEffect, useRef, useState } from "react";
// ATE-CR-164 — useParams reads `verificationId` from the URL so a
// refresh / shareable link re-hydrates the status panel; useNavigate
// pushes the id into the URL after a successful submit.
import { useNavigate, useParams } from "react-router-dom";

import {
  getVerificationStatus,
  listVerificationRecipients,
  startVerification,
  type StartVerificationResponse,
  type VerificationRecipient,
  type VerificationStage,
  type VerificationStageStatus,
  type VerificationStatusResponse,
} from "@/api/verifications.api";
import { useToast } from "@/components/common/useToast";
import { useMe } from "@/hooks/useMe";

// ATE-CR-159
const STAGE_LABELS: Record<VerificationStage["name"], string> = {
  published: "Published",
  consumed: "Consumed",
  phase_1_persist: "Phase 1: Persist",
  phase_2_email: "Phase 2: Email",
  graph_api_send: "Graph API send",
};

// ATE-CR-159 — visual marker per status. Kept as a plain glyph map so the
// page has zero new dependencies; lucide-react is available but the
// stepper renders fine with text + ARIA roles.
function statusIcon(status: VerificationStageStatus): string {
  switch (status) {
    case "pending":
      return "○";
    case "running":
      return "◔";
    case "success":
      return "●";
    case "failed":
      return "✕";
  }
}

function statusClassName(status: VerificationStageStatus): string {
  return `verifications-page__stage verifications-page__stage--${status}`;
}

// ATE-CR-159
export function VerificationsPage(): JSX.Element {
  const { me, loading: meLoading } = useMe();
  const { toast } = useToast();
  const isSuperAdmin = me?.system_role === "SUPER_ADMIN";

  // ATE-CR-164 — URL state. `verificationId` may be undefined when the
  // page mounts at the bare /admin/notifications/verifications route;
  // hydration only fires when it's present.
  const navigate = useNavigate();
  const { verificationId } = useParams<{ verificationId?: string }>();

  const [recipients, setRecipients] = useState<
    readonly VerificationRecipient[]
  >([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState<Error | null>(null);
  const [selected, setSelected] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  const [verification, setVerification] =
    useState<StartVerificationResponse | null>(null);
  const [status, setStatus] = useState<VerificationStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load recipients on mount (admin only)
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    const ctrl = new AbortController();
    setRecipientsLoading(true);
    setRecipientsError(null);

    listVerificationRecipients(ctrl.signal)
      .then((res) => {
        if (cancelled) return;
        setRecipients(res.recipients);
        if (res.recipients.length > 0) {
          setSelected(res.recipients[0].email);
        }
        setRecipientsLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setRecipientsError(err);
        setRecipientsLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [isSuperAdmin]);

  // Cleanup any pending auto-refresh timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  // ATE-CR-164 — Hydrate the status panel from the URL on mount /
  // refresh / shareable deep-link. Idempotent: only seeds once when
  // `verificationId` is present AND no verification is loaded yet.
  // Synthesises a minimal `StartVerificationResponse`-shaped object so
  // the existing render gate (`verification !== null`) flips on; the
  // real recipient/trace/published_at fields are filled by the status
  // panel from the GET response.
  // ATE-CR-164
  useEffect(() => {
    // ATE-CR-164
    if (!isSuperAdmin) return;
    // ATE-CR-164
    if (verificationId === undefined || verificationId === "") return;
    // ATE-CR-164
    if (verification !== null) return;
    // ATE-CR-164
    setVerification({
      verification_id: verificationId,
      trace_id: "",
      recipient: "",
      published_at: "",
    });
    // ATE-CR-164
    void fetchAndScheduleStatus(verificationId);
    // ATE-CR-164 — fetchAndScheduleStatus is hoisted (function decl);
    // depending on it would force a useCallback rewrite the spec
    // explicitly avoids. Effect re-fires only when `verificationId`
    // (URL) or `isSuperAdmin` (auth gate) flips, both of which are the
    // intended triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationId, isSuperAdmin]);

  if (meLoading) {
    return <div aria-busy="true">Loading…</div>;
  }

  if (!isSuperAdmin) {
    return (
      <div role="alert" className="verifications-page__forbidden">
        Forbidden — super_admin only
      </div>
    );
  }

  // ATE-CR-159 — fetch status and (re)schedule auto-refresh based on
  // Retry-After. Always clears any prior timer first to keep at most
  // one in flight.
  async function fetchAndScheduleStatus(verificationId: string): Promise<void> {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setStatusLoading(true);
    try {
      const { data, retryAfterSeconds } =
        await getVerificationStatus(verificationId);
      setStatus(data);
      if (retryAfterSeconds !== null && retryAfterSeconds > 0) {
        refreshTimerRef.current = setTimeout(() => {
          void fetchAndScheduleStatus(verificationId);
        }, retryAfterSeconds * 1000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast("error", `Failed to fetch status: ${msg}`);
    } finally {
      setStatusLoading(false);
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await startVerification({ recipient: selected });
      setVerification(res);
      setStatus(null);
      // ATE-CR-164 — push the new verification_id into the URL so a
      // refresh re-hydrates the same panel and the URL is shareable.
      // Pushed (not replaced) so the back button returns to the empty
      // form. Idempotent against the hydration effect above because
      // `verification !== null` after `setVerification(res)`.
      navigate(`/admin/notifications/verifications/${res.verification_id}`, {
        replace: false,
      });
      toast(
        "success",
        `Verification published: ${res.verification_id.slice(0, 8)}…`,
      );
      await fetchAndScheduleStatus(res.verification_id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast("error", `Failed to start verification: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  function handleRefresh(): void {
    if (verification === null) return;
    void fetchAndScheduleStatus(verification.verification_id);
  }

  const noRecipients = !recipientsLoading && recipients.length === 0;

  return (
    <div className="verifications-page">
      <header className="verifications-page__header">
        <h1 className="verifications-page__title">Email Verifications</h1>
      </header>

      {recipientsLoading && (
        <div aria-busy="true" className="verifications-page__loading">
          Loading recipients…
        </div>
      )}

      {recipientsError && (
        <div role="alert" className="verifications-page__error">
          Failed to load recipients: {recipientsError.message}
        </div>
      )}

      {noRecipients && (
        <div role="alert" className="verifications-page__empty">
          No recipients configured. VERIFY_EMAIL_ALLOWLIST is empty for this
          env.
        </div>
      )}

      <section
        className="verifications-page__form"
        aria-label="Send verification"
      >
        <label htmlFor="verifications-recipient">Recipient</label>
        <select
          id="verifications-recipient"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={noRecipients || submitting}
        >
          {recipients.map((r) => (
            <option key={r.email} value={r.email}>
              {r.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void handleSubmit()}
          disabled={noRecipients || submitting || !selected}
        >
          {submitting ? "Sending…" : "Send verification"}
        </button>
      </section>

      {verification !== null && (
        <section
          className="verifications-page__status"
          aria-label="Verification status"
        >
          <div className="verifications-page__status-header">
            <h2>Status</h2>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleRefresh}
              disabled={statusLoading}
              aria-label="Refresh status"
            >
              {statusLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <dl className="verifications-page__meta">
            <dt>Verification ID</dt>
            <dd>
              <code>{verification.verification_id}</code>
            </dd>
            <dt>Trace ID</dt>
            <dd>
              <code>{verification.trace_id}</code>
            </dd>
            <dt>Recipient</dt>
            <dd>{verification.recipient}</dd>
          </dl>

          {status?.trace_expired === true && (
            <div role="alert" className="verifications-page__banner">
              Trace expired (&gt;30d). Pipeline data unavailable.
            </div>
          )}

          {status !== null && (
            <a
              href={status.trace_console_url}
              target="_blank"
              rel="noopener noreferrer"
              className="verifications-page__trace-link"
            >
              Open in Cloud Trace
            </a>
          )}

          <ol className="verifications-page__timeline">
            {(status?.stages ?? []).map((stage) => (
              <li key={stage.name} className={statusClassName(stage.status)}>
                <span
                  aria-hidden="true"
                  className="verifications-page__stage-icon"
                >
                  {statusIcon(stage.status)}
                </span>
                <span className="verifications-page__stage-label">
                  {STAGE_LABELS[stage.name]}
                </span>
                <span className="verifications-page__stage-status">
                  {stage.status}
                </span>
                {stage.at !== null && (
                  <time
                    dateTime={stage.at}
                    className="verifications-page__stage-at"
                  >
                    {new Date(stage.at).toLocaleString()}
                  </time>
                )}
                {stage.duration_ms !== null && (
                  <span className="verifications-page__stage-duration">
                    {stage.duration_ms}ms
                  </span>
                )}
                {stage.error !== null && (
                  <span
                    className="verifications-page__stage-error"
                    title={stage.error}
                  >
                    {stage.error}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
